import React, { FC } from 'react';
import css from '../TemplatePage.scss';
import { Button, TextButton } from '@wix/design-system';
import { DataHooks } from '../../../constants/dataHooks';
import { useSingleTemplateData } from '../../../hooks/useSingleTemplateData';
import { useTranslation } from '@wix/wix-i18n-config';
import classNames from 'classnames';
import { ActiveModal, ModalRenderer } from '../Modals/ModalRenderer';
import { useCloneTemplate } from '../../../hooks/useCloneTemplate';
import { shouldShowGitHubLink } from '../../../utils/shouldShowGitHubLink';
import { useTemplatesPageBi } from '../../../hooks/useTemplatesPageBi';

type Props = {
  isStickyHeaderCTAs?: boolean;
  initialActiveModal?: ActiveModal;
};
export const MainCTAs: FC<Props> = ({
  isStickyHeaderCTAs = false,
  initialActiveModal = ActiveModal.NONE,
}) => {
  const { template } = useSingleTemplateData();
  const { t } = useTranslation();
  const showGitHubLink = shouldShowGitHubLink(template);
  const [activeModal, setActiveModal] =
    React.useState<ActiveModal>(initialActiveModal);
  const cloneTemplate = useCloneTemplate(setActiveModal);
  const { reportTemplatePageCTAClick } = useTemplatesPageBi();

  const onCloneTemplateClick = () => {
    reportTemplatePageCTAClick({
      CtaName: 'clone template',
      templateId: template.id,
    });
    cloneTemplate();
  };

  const previewTemplate = () => {
    const destUrl = template.previewUrl;
    reportTemplatePageCTAClick({
      CtaName: 'preview template',
      destUrl,
      templateId: template.id,
    });
    return window.open(destUrl);
  };
  const openGitHubLink = () => {
    const destUrl = template.repositoryUrl!;
    reportTemplatePageCTAClick({
      CtaName: 'open github',
      destUrl,
      templateId: template.id,
    });
    return window.open(destUrl);
  };

  return (
    <div
      data-hook={DataHooks.MainCTAsWrapper}
      className={classNames(css.templateActionsBar, {
        [css.reverse]: isStickyHeaderCTAs,
      })}
    >
      <Button
        size={isStickyHeaderCTAs ? 'small' : 'large'}
        dataHook={DataHooks.CloneTemplateButton(isStickyHeaderCTAs)}
        onClick={onCloneTemplateClick}
      >
        {t('app.template-page.button.use-template')}
      </Button>
      {template?.previewUrl && (
        <Button
          size={isStickyHeaderCTAs ? 'small' : 'large'}
          priority="secondary"
          dataHook={DataHooks.PreviewButton(isStickyHeaderCTAs)}
          onClick={previewTemplate}
        >
          {t('app.template-page.button.preview')}
        </Button>
      )}
      {showGitHubLink && (
        <TextButton
          size={isStickyHeaderCTAs ? 'small' : 'medium'}
          weight="thin"
          underline="always"
          dataHook={DataHooks.GitHubLink(isStickyHeaderCTAs)}
          className={classNames(css.gitHubLink, {
            [css.stickyGitHubLink]: isStickyHeaderCTAs,
          })}
          onClick={openGitHubLink}
        >
          {t('app.template-page.button.github-link')}
        </TextButton>
      )}
      <ModalRenderer
        activeModal={activeModal}
        setActiveModal={setActiveModal}
      />
    </div>
  );
};
